import { FormEvent, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  useCreateChapterMutation,
  useDeleteChapterMutation,
  useGetChaptersQuery,
  useUpdateChapterMutation,
  useGetStatesQuery,
  Chapter,
} from "../api/chapterApi";
import {
  MapPin,
  RefreshCw,
  Plus,
  Edit2,
  Trash2,
  Eye,
  Check,
  X,
  AlertCircle,
  CheckCircle2,
  Globe,
  Search,
} from "lucide-react";

// Security & UI Patterns
import PageGate from "../../../components/PageGate";
import AccessGate from "../../../components/AccessGate";
import { PERMS } from "../../../utils/perms";

/**
 * Reusable Stat Card matching Admin Pattern
 */
function StatCard({ title, value }: { title: string; value: string | number }) {
  return (
    <div className="bg-white border rounded-xl p-5 shadow-sm">
      <div className="text-sm text-gray-600">{title}</div>
      <div className="text-2xl font-semibold text-gray-900 mt-1">{value}</div>
    </div>
  );
}

export default function ChaptersPage() {
  const {
    data: chapters = [],
    isLoading,
    error,
    refetch,
    isFetching,
  } = useGetChaptersQuery();

  const { data: states = [], isLoading: statesLoading } = useGetStatesQuery();

  // API Mutations
  const [createChapter, { isLoading: creating }] = useCreateChapterMutation();
  const [updateChapter, { isLoading: updating }] = useUpdateChapterMutation();
  const [deleteChapter, { isLoading: deleting }] = useDeleteChapterMutation();
  const busy = creating || updating || deleting;

  // Filter State
  const [q, setQ] = useState("");
  const [stateFilter, setStateFilter] = useState("all");

  // Inline Edit State
  const [editId, setEditId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ name: "", state_id: "" });

  // Creation State
  const [createForm, setCreateForm] = useState({ name: "", state_id: "" });
  const [msg, setMsg] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const stateNameById = useMemo(() => {
    const m = new Map<string, string>();
    states.forEach((s: any) => m.set(String(s.id), String(s.name)));
    return m;
  }, [states]);

  // Search & Filter Logic (Matching Admin Pattern)
  const filteredChapters = useMemo(() => {
    const nq = q.trim().toLowerCase();
    return chapters.filter((c: Chapter) => {
      const stateName = stateNameById.get(String(c.state_id)) || "";
      const blob = `${c.name} ${stateName}`.toLowerCase();

      const matchesSearch = !nq || blob.includes(nq);
      const matchesState =
        stateFilter === "all" || String(c.state_id) === stateFilter;

      return matchesSearch && matchesState;
    });
  }, [chapters, q, stateFilter, stateNameById]);

  // Stats Logic
  const stats = useMemo(() => {
    return {
      total: chapters.length,
      filtered: filteredChapters.length,
      statesCovered: new Set(chapters.map((c) => c.state_id)).size,
    };
  }, [chapters, filteredChapters]);

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    setMsg(null);
    const stateId = Number(createForm.state_id);
    if (!stateId) return setMsg({ type: "error", text: "Select a state" });

    try {
      await createChapter({
        name: createForm.name.trim(),
        state_id: stateId,
      }).unwrap();
      setCreateForm({ name: "", state_id: "" });
      setMsg({ type: "success", text: "Chapter created successfully" });
    } catch (err: any) {
      setMsg({ type: "error", text: err?.data?.message || "Create failed" });
    }
  };

  const startEdit = (c: Chapter) => {
    setEditId(String(c.id));
    setEditForm({ name: c.name || "", state_id: String(c.state_id || "") });
  };

  const onSaveEdit = async () => {
    if (!editId) return;
    try {
      await updateChapter({
        id: editId,
        name: editForm.name.trim(),
        state_id: Number(editForm.state_id),
      }).unwrap();
      setEditId(null);
      setMsg({ type: "success", text: "Chapter updated" });
    } catch (err: any) {
      setMsg({ type: "error", text: err?.data?.message || "Update failed" });
    }
  };

  const onDelete = async (id: string) => {
    if (!window.confirm("Delete this chapter?")) return;
    try {
      await deleteChapter(id).unwrap();
      setMsg({ type: "success", text: "Chapter deleted" });
    } catch (err: any) {
      setMsg({ type: "error", text: err?.data?.message || "Delete failed" });
    }
  };

  return (
    <PageGate perm={PERMS.chapter?.access || PERMS.admin.access}>
      <div className="space-y-6">
        {/* Header & Create Form Card */}
        <div className="bg-white border rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-gray-50 border text-gray-700">
                <Globe className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Chapters</h1>
                <p className="text-sm text-gray-500">
                  Registry of regional branches
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => refetch()}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm"
              >
                <RefreshCw
                  className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`}
                />
                Refresh
              </button>
            </div>
          </div>

          {msg && (
            <div
              className={`mt-4 flex items-center gap-2 px-4 py-2 rounded-lg border text-sm ${
                msg.type === "success"
                  ? "bg-green-50 border-green-100 text-green-700"
                  : "bg-red-50 border-red-100 text-red-700"
              }`}
            >
              {msg.type === "success" ? (
                <CheckCircle2 className="w-4 h-4" />
              ) : (
                <AlertCircle className="w-4 h-4" />
              )}
              {msg.text}
            </div>
          )}

          {/* Quick Create Form */}
          <AccessGate perm={PERMS.chapter?.create || PERMS.admin.create}>
            <form
              onSubmit={onCreate}
              className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-3 items-end border-t pt-6"
            >
              <div className="md:col-span-5">
                <label className="text-[11px] font-bold text-gray-500 uppercase mb-1 block">
                  New Chapter Name
                </label>
                <input
                  value={createForm.name}
                  onChange={(e) =>
                    setCreateForm((s) => ({ ...s, name: e.target.value }))
                  }
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-100 outline-none"
                  placeholder="Enter name..."
                  required
                />
              </div>
              <div className="md:col-span-4">
                <label className="text-[11px] font-bold text-gray-500 uppercase mb-1 block">
                  Assign State
                </label>
                <select
                  value={createForm.state_id}
                  onChange={(e) =>
                    setCreateForm((s) => ({ ...s, state_id: e.target.value }))
                  }
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-gray-100 outline-none"
                  required
                >
                  <option value="">Select State</option>
                  {states.map((s: any) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-3">
                <button
                  disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-gray-900 text-white px-4 py-2 text-sm font-bold hover:bg-black transition-all"
                >
                  <Plus className="w-4 h-4" />
                  {creating ? "Adding..." : "Add Chapter"}
                </button>
              </div>
            </form>
          </AccessGate>

          {/* Search/Filter Bar (Admin Style) */}
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-3 items-end border-t pt-6">
            <div className="lg:col-span-8">
              <label className="block text-[11px] font-semibold text-gray-500 uppercase mb-1">
                Search Registry
              </label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Filter by chapter name or state..."
                  className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-100"
                />
              </div>
            </div>
            <div className="lg:col-span-3">
              <label className="block text-[11px] font-semibold text-gray-500 uppercase mb-1">
                State Filter
              </label>
              <select
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-gray-100"
              >
                <option value="all">All States</option>
                {states.map((s: any) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="lg:col-span-1">
              <button
                onClick={() => {
                  setQ("");
                  setStateFilter("all");
                }}
                className="w-full px-2 py-2 border rounded-lg hover:bg-gray-50 text-sm"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard title="Total Chapters" value={stats.total} />
          <StatCard title="States Active" value={stats.statesCovered} />
          <StatCard title="Filtered View" value={stats.filtered} />
        </div>

        {/* Table Card */}
        <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="py-20 text-center text-gray-400">
              Loading chapters...
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="bg-gray-50 border-b text-gray-400 font-medium uppercase text-[11px]">
                    <th className="py-4 px-6 w-16">#</th>
                    <th className="py-4 px-6">Chapter Name</th>
                    <th className="py-4 px-6">State Jurisdiction</th>
                    <th className="py-4 px-6 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredChapters.map((c: Chapter, idx: number) => {
                    const isEditing = editId === String(c.id);
                    const stateName =
                      c.state_name ||
                      stateNameById.get(String(c.state_id)) ||
                      "—";

                    return (
                      <tr
                        key={String(c.id)}
                        className="hover:bg-gray-50/50 transition-colors"
                      >
                        <td className="py-4 px-6 text-gray-400 font-mono">
                          {(idx + 1).toString().padStart(2, "0")}
                        </td>
                        <td className="py-4 px-6">
                          {isEditing ? (
                            <input
                              className="w-full border rounded px-2 py-1 text-sm outline-none focus:border-blue-500"
                              value={editForm.name}
                              onChange={(e) =>
                                setEditForm((s) => ({
                                  ...s,
                                  name: e.target.value,
                                }))
                              }
                            />
                          ) : (
                            <div className="font-semibold text-gray-900 flex items-center gap-2">
                              <MapPin className="w-3.5 h-3.5 text-gray-400" />
                              {c.name}
                            </div>
                          )}
                        </td>
                        <td className="py-4 px-6">
                          {isEditing ? (
                            <select
                              className="w-full border rounded px-2 py-1 text-sm bg-white"
                              value={editForm.state_id}
                              onChange={(e) =>
                                setEditForm((s) => ({
                                  ...s,
                                  state_id: e.target.value,
                                }))
                              }
                            >
                              {states.map((s: any) => (
                                <option key={s.id} value={s.id}>
                                  {s.name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[11px] font-bold uppercase">
                              {stateName}
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-6 text-right">
                          <div className="flex justify-end gap-2">
                            {isEditing ? (
                              <>
                                <button
                                  onClick={onSaveEdit}
                                  className="p-1.5 border rounded-lg bg-green-600 text-white"
                                >
                                  <Check className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => setEditId(null)}
                                  className="p-1.5 border rounded-lg bg-gray-100"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </>
                            ) : (
                              <>
                                <Link
                                  to={`/chapter/chapters/${c.id}`}
                                  className="p-1.5 border rounded-lg hover:bg-gray-50"
                                >
                                  <Eye className="w-4 h-4 text-gray-600" />
                                </Link>
                                <AccessGate
                                  perm={
                                    PERMS.chapter?.update || PERMS.admin.toggle
                                  }
                                >
                                  <button
                                    onClick={() => startEdit(c)}
                                    className="p-1.5 border rounded-lg hover:bg-gray-50"
                                  >
                                    <Edit2 className="w-4 h-4 text-gray-600" />
                                  </button>
                                </AccessGate>
                                <AccessGate
                                  perm={
                                    PERMS.chapter?.delete || PERMS.admin.delete
                                  }
                                >
                                  <button
                                    onClick={() => onDelete(String(c.id))}
                                    className="p-1.5 border rounded-lg hover:bg-red-50 text-red-600"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </AccessGate>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filteredChapters.length === 0 && (
                <div className="py-20 text-center text-gray-500 italic">
                  No matching chapters found.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </PageGate>
  );
}
